import React from "react";
import { VintomError } from "src/api/model/error/VintomError";
import { Subtitle } from "../../../..//model/playerConfiguration/Subtitle";
import { I18n } from "../../../../i18n/i18n";
import { PlayerControls } from "../../../../model/playerConfiguration/PlayerControls";
import { ScenarioElement } from "../../../../model/playerConfiguration/ScenarioElement";
import { ScenarioPath } from "../../../../model/scenarioPath/ScenarioPath";
import { SubtitlesText } from "../../../../model/subtitles/SubtitlesText";
import { MobileOverlayState } from "../../../../uiModel/mobileOverlay/MobileOverlayState";
import { ScenarioElementPlaybackState } from "../../../../uiModel/playback/ScenarioElementPlaybackState";
import { SocialShareConfiguration } from "../../../../uiModel/socialShare/SocialShareConfiguration";
import { VolumeMutedIconComponent } from "../../../icons/volume/VolumeMutedIconComponent";
import { MenuButtonComponent } from "../common/MenuButtonComponent";
import { SubtitlesDisplayComponent } from "../common/SubtitlesDisplayComponent";
import { MobileMenuComponent } from "./MobileMenuComponent";
import { MobileOverlayComponent } from "./MobileOverlayComponent";
import { MobileSeekbarComponent } from "./MobileSeekbarComponent";

import "@styles/c-mobileControlsLayer.scss";

interface Props {
  i18n: I18n;
  configuration: PlayerControls;
  isLoading: boolean;
  isCTADisplayed: boolean;
  socialShareConfiguration: SocialShareConfiguration;

  playbackState: ScenarioElementPlaybackState;
  onPlay: () => void;
  onPause: () => void;
  onReplay: () => void;
  onSeekStart: () => void;
  onSeekEnd: () => void;

  scenarioPath: ScenarioPath;
  totalTime: number;
  ellapsedTime: number;
  bufferedTime: number;
  onSeekTo: (time: number) => void;
  onJumpToScenarioElement: (scenarioElement: ScenarioElement, progress: number) => void;

  isVolumeMuted: boolean;
  onToggleMuted: () => void;

  subtitles: Subtitle[];
  selectedSubtitlesSrc?: string;
  subtitlesText?: SubtitlesText;
  onSubtitleSourceSelected: (subtitle?: Subtitle) => void;

  isFullscreenSupported: boolean;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;

  message?: string | VintomError;
  onSendError: (error: VintomError) => void;
  playIconOnPoster: boolean;
  disableIcon: boolean;
}

interface State {
  isSeeking: boolean;
  isControlsShownAfterPlayerClicked: boolean;
  isSubtitlesSelectorShown: boolean;
  isSocialShareShown: boolean;
}

export class MobileControlsLayerComponent extends React.Component<Props, State> {

  private static SHOW_CONTROLS_TIMEOUT = 2000;

  private hideControlsTimeoutHandle?: number;

  constructor(props: Props) {
    super(props);
    this.state = {
      isSeeking: false,
      isControlsShownAfterPlayerClicked: true,
      isSubtitlesSelectorShown: false,
      isSocialShareShown: false
    };
  }

  render() {
    const {
      i18n,
      configuration,
      socialShareConfiguration,

      scenarioPath,
      totalTime,
      ellapsedTime,
      bufferedTime,
      onSeekTo,
      onJumpToScenarioElement,

      isVolumeMuted,
      onToggleMuted,

      subtitles,
      selectedSubtitlesSrc,
      subtitlesText,

      isFullscreenSupported,
      isFullscreen,
      onToggleFullscreen,

      message,
      onSendError,
      disableIcon
    } = this.props;

    const overlayState = this.calculateOverlayState();

    return (
      <div className="c-mobileControlsLayer">
        <div className={`c-mobileControlsLayer__overlayLayer
            ${this.isOverlayDisplayed && configuration.playPauseEnabled ? "" : "c-mobileControlsLayer__overlayLayer--hidden"}`}
        >
          {this.isOverlayDisplayed &&
            <MobileOverlayComponent
              i18n={i18n}
              controlState={overlayState}
              onUnmute={onToggleMuted}
              subtitles={subtitles}
              selectedSubtitlesSrc={selectedSubtitlesSrc}
              onSubtitleSourceSelected={(subtitle) => this.subtitleSourceSelected(subtitle)}
              socialShareConfiguration={socialShareConfiguration}
              onShared={() => this.handleShared()}
              message={message}
              onSendError={onSendError}
              playIconOnPoster={configuration.playIconOnPoster}
              disableIcon={disableIcon}
            />
          }
        </div>
        <div
          className={`c-mobileControlsLayer__logoAndMenuLayer
            ${this.isControlsDisplayed && configuration.controlbarEnabled ? "" : "c-mobileControlsLayer__logoAndMenuLayer--hidden"}`}
        >
          <div className="c-mobileControlsLayer__logo">
            {configuration.logo &&
              <img
                className="c-mobileControlsLayer__logoImage"
                src={configuration.logo}
              />
            }
          </div>
          <div className="c-mobileControlsLayer__spacer" />
          <div className="c-mobileControlsLayer__menu">
            <MobileMenuComponent
              isSocialShareDisplayed={configuration.socialButtonEnabled && socialShareConfiguration.canShare}
              onSocialShareClicked={() => this.handleSocialShareClicked()}

              isFullscreenSupported={isFullscreenSupported}
              isFullscreen={isFullscreen}
              onToggleFullscreen={onToggleFullscreen}

              isVolumeMuted={isVolumeMuted}
              onToggleMuted={onToggleMuted}

              hasSubtitles={subtitles && !!subtitles.length}
              selectedSubtitlesSrc={selectedSubtitlesSrc}
              onSubtitlesSelectorClicked={() => this.handleSubtitlesSelectorClicked()}

              isDisabled={!!message}
            />
          </div>
          {isVolumeMuted &&
            <MenuButtonComponent
              className={`c-mobileControlsLayer__unMuteButton
                ${this.isControlsDisplayed ? "" : "c-mobileControlsLayer__unMuteButton--hidden"}`}
              onClick={onToggleMuted}
              icon={<VolumeMutedIconComponent />}
              isDisabled={!!message}
            />
          }
        </div>
        {subtitlesText &&
          <div className="c-mobileControlsLayer__subtitlesLayer">
            <div
              className="c-mobileControlsLayer__subtitlesTopSpacer"
              style={{
                flexBasis: isFullscreen ? "100%" : `${subtitlesText.verticalPosition}%`
              }}
            />
            <div className={`c-mobileControlsLayer__subtitles ${this.isControlsDisplayed ? "" : "c-mobileControlsLayer__subtitles--hidden"}`}>
              <SubtitlesDisplayComponent
                subtitlesText={subtitlesText.text}
              />
            </div>
            <div
              className={`c-mobileControlsLayer__subtitlesBottomSpacer ${this.isControlsDisplayed ?
                "c-mobileControlsLayer__subtitlesBottomSpacer--withControls" : ""}`}
            />
          </div>
        }
        {scenarioPath &&
          <div
            className={`c-mobileControlsLayer__seekbarLayer ${this.isControlsDisplayed && configuration.seekbarEnabled ?
              "" : "c-mobileControlsLayer__seekbarLayer--hidden"}`}
          >
            <div className="c-mobileControlsLayer__seekbar">
              <MobileSeekbarComponent
                isHidden={!this.isControlsDisplayed}
                scenarioPath={scenarioPath}
                totalTime={totalTime}
                ellapsedTime={ellapsedTime}
                bufferedTime={bufferedTime}
                onSeekTo={onSeekTo}
                onJumpToScenarioElement={onJumpToScenarioElement}
                onSliderInteractionStarted={() => this.handleSeekStart()}
                onSliderInteractionEnded={(wasChanging) => this.handleSeekEnd(wasChanging)}
                isDisabled={!!message}
              />
            </div>
          </div>
        }
      </div>
    );
  }

  handleUserInteractedWithPlayer() { }

  handleUserInteractionEnded() { }

  handleUserClickedPlayer() {
    this.setState({
      isSubtitlesSelectorShown: false,
      isSocialShareShown: false
    });
    const { playbackState, onPlay, onReplay, onPause } = this.props;

    switch (playbackState) {
      case "paused":
        this.hideControls();
        onPlay();
        break;
      case "scenarioFinished":
        this.hideControls();
        onReplay();
        break;
      case "elementFinished":
        this.resetTimerAndShowControls();
        break;
      case "playing":
        if (this.state.isControlsShownAfterPlayerClicked) {
          if (this.hideControlsTimeoutHandle !== undefined) {
            clearTimeout(this.hideControlsTimeoutHandle);
          }
          onPause();
        } else {
          this.resetTimerAndShowControls();
        }
        break;
      default:
      // do nothing;
    }
  }

  private handleSeekStart() {
    this.setState({
      isSeeking: true,
      isSubtitlesSelectorShown: false,
      isSocialShareShown: false
    });
    this.props.onSeekStart();
  }

  private handleSeekEnd(wasChanging: boolean) {
    this.setState({
      isSeeking: false
    });

    if (wasChanging) {
      this.props.onSeekEnd();
    }
  }

  private calculateOverlayState(): MobileOverlayState {
    const { isLoading, isVolumeMuted, playbackState, message, isCTADisplayed } = this.props;

    if (this.state.isSocialShareShown) {
      return "socialSelector";
    }
    if (this.state.isSubtitlesSelectorShown) {
      return "subtitlesSelector";
    }
    if (message) {
      return "message";
    }
    if (isLoading) {
      return "loading";
    }
    if (isVolumeMuted) {
      return "unmute";
    }
    if (playbackState === "paused") {
      return "pause";
    }
    if (playbackState === "scenarioFinished" && !isCTADisplayed) {
      return "replay";
    }
    if (playbackState === "playing") {
      return "play";
    }
  }

  private get isOverlayDisplayed() {
    return this.props.isLoading || this.isControlsDisplayed;
  }

  private get isControlsDisplayed() {
    return this.state.isControlsShownAfterPlayerClicked ||
      this.state.isSeeking ||
      this.state.isSubtitlesSelectorShown ||
      this.state.isSocialShareShown ||
      !!this.props.message ||
      this.props.playbackState === "scenarioFinished" && !this.props.isCTADisplayed;
  }

  private handleSocialShareClicked() {
    this.setState({
      isSubtitlesSelectorShown: false,
      isSocialShareShown: !this.state.isSocialShareShown
    });
  }

  private handleSubtitlesSelectorClicked() {
    const { subtitles, selectedSubtitlesSrc, onSubtitleSourceSelected } = this.props;
    this.setState({
      isSocialShareShown: false
    });
    if (subtitles.length === 1) {
      if (selectedSubtitlesSrc) {
        onSubtitleSourceSelected(undefined);
      } else {
        onSubtitleSourceSelected(subtitles[0]);
      }
    } else {
      this.setState({
        isSubtitlesSelectorShown: !this.state.isSubtitlesSelectorShown
      });
    }
  }

  private subtitleSourceSelected(subtitle: Subtitle) {
    this.props.onSubtitleSourceSelected(subtitle);
    this.setState({
      isSubtitlesSelectorShown: false
    });
  }

  private handleShared() {
    this.setState({
      isSocialShareShown: false
    });
  }

  private resetTimerAndShowControls() {
    if (this.hideControlsTimeoutHandle !== undefined) {
      clearTimeout(this.hideControlsTimeoutHandle);
    }
    this.hideControlsTimeoutHandle = window.setTimeout(() => this.hideControls(), MobileControlsLayerComponent.SHOW_CONTROLS_TIMEOUT);
    this.setState({
      isControlsShownAfterPlayerClicked: true
    });
  }

  private hideControls() {
    this.setState({
      isControlsShownAfterPlayerClicked: false
    });
  }
}
